/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { Else, If, Then } from "react-if";
import { theme } from "@nordcloud/gnui";
import { ChartType } from "~/generated/graphql";
import {
  BrickLoader,
  UniversalWrap,
  ChartType as TableChartType,
  Granularity,
  Wrapper,
  DateRange,
  LoaderWrap,
} from "~/components";
import { EstatePeriod } from "~/constants";
import { useCurrency } from "~/hooks";
import { getEstateTotalsTableColumns, EstateCostTable } from "../Views";
import { AccumulatedCostData } from "./AccumulatedCostData";
import { EstateRecordsGroupingChart } from "./EstateRecordsGroupingChart";
import { getAverageCost } from "./helpers";
import { useEstateRecordsCost, useEstateRecordsGroupByChartJob } from "./hooks";

type Props = {
  granularity: Granularity;
  period: EstatePeriod;
  isCostLoading: boolean;
  estateRecordsCount: number;
  range: DateRange;
  setIsChartDataLoading: Dispatch<SetStateAction<boolean>>;
  currentMonthTotal?: string;
  monthlySavings?: string | null;
  costForecast?: string;
};

export function EstateRecordsAccumulatedCostChart({
  granularity,
  period,
  range,
  isCostLoading,
  estateRecordsCount,
  setIsChartDataLoading,
  currentMonthTotal,
  monthlySavings,
  costForecast,
}: Props) {
  const { currency } = useCurrency();

  const {
    isChartDataLoading,
    chartData,
    chartError,
    tableData,
    startDate,
    endDate,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Accumulated,
    range,
  });

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    totalCost,
    stackBarColors,
    chartKeys,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period,
    startDate,
    endDate,
    chartType: TableChartType.ACCUMULATED_COST,
  });

  useEffect(() => {
    setIsChartDataLoading(isChartDataLoading);
  }, [isChartDataLoading]);

  const columns = useMemo(
    () =>
      getEstateTotalsTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerGroupTableData,
        period,
        colors: stackBarColors,
        chartType: TableChartType.ACCUMULATED_COST,
      }),
    [
      startDate,
      endDate,
      currency,
      costPerGroupTableData,
      period,
      stackBarColors,
    ]
  );

  return (
    <Wrapper
      css={{ margin: theme.spacing.spacing02 }}
      data-testid="accumulated-cost-chart-wrapper"
      id="uf-er-chart"
    >
      <UniversalWrap
        loaderProps={{ isChartDataLoading, Component: BrickLoader }}
        errorProps={{ error: chartError }}
      >
        <EstateRecordsGroupingChart
          data={costPerGroupChartData}
          granularity={granularity}
          loading={isChartDataLoading}
          startDate={startDate}
          endDate={endDate}
          period={period}
          colors={stackBarColors}
          keys={chartKeys}
          chartType={TableChartType.ACCUMULATED_COST}
        />
      </UniversalWrap>
      <If condition={period === EstatePeriod.RANGE}>
        <Then>
          <AccumulatedCostData
            period={period}
            range={range}
            totalCost={totalCost}
            isLoading={isChartDataLoading}
            averageCost={getAverageCost(totalCost, startDate, endDate)}
          />
          <div css={{ width: "100%", overflow: "scroll" }}>
            <LoaderWrap loading={isChartDataLoading} Component={BrickLoader}>
              <EstateCostTable
                data={costPerGroupTableData}
                columns={columns}
                period={EstatePeriod.DEFAULT_MONTH}
              />
            </LoaderWrap>
          </div>
        </Then>
        <Else>
          <AccumulatedCostData
            period={period}
            range={range}
            isLoading={isCostLoading}
            totalCost={Number(currentMonthTotal)}
            monthlySavings={monthlySavings}
            count={estateRecordsCount}
            forecast={costForecast}
          />
        </Else>
      </If>
    </Wrapper>
  );
}
